import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { Archive, ArchiveRestore, MoreHorizontal, Pencil } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpPopover
} from '@/design';
import { ApiError, errorMessage } from '@/domains/api';
import { useCan, type Can } from '@/domains/auth';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { type IMailTemplate, type MailTemplateGroup, type MailUsage } from '../../api/mail-template-contracts';
import { fieldRefusal, refusalMessage } from '../../refusal';
import { TagInput, TagsHelp, USAGES, useMailTags } from './template-tags';

/**
 * Custom templates' own controls (SR-523 stages 3 and 5): the New template dialog — name,
 * purpose, where it can be used, tags — and the header's menu — its details, Archive, Restore.
 */

/** Who edits a template: a custom one used only from Lists `mail.outreach.author` or
 *  `mail.templates`; one that reminders use, and every built-in one, `mail.templates`. */
export function canEditTemplate(
  can: Can,
  template: { group?: Nullable<MailTemplateGroup>; usages?: readonly MailUsage[] }
): boolean {
  if (can('mail.templates')) return true;
  return template.group === 'custom' && !(template.usages ?? []).includes('reminders') && can('mail.outreach.author');
}

/** Why a usage cannot go: the campaigns still to send and the reminder steps yet to fire on it. */
function inUseWords(error: ApiError): string {
  const uses = (error.problem as { steps?: { stepId?: Nullable<string>; campaignId?: Nullable<string> }[] } | undefined)
    ?.steps;
  const campaigns = (uses ?? []).filter((use) => use.campaignId).length;
  const steps = (uses ?? []).filter((use) => use.stepId).length;
  const what = [
    campaigns > 0 && `${campaigns} campaign${campaigns === 1 ? '' : 's'} still to send`,
    steps > 0 && `${steps} reminder step${steps === 1 ? '' : 's'} yet to fire`
  ].filter(Boolean);
  return what.length > 0
    ? `That use cannot go yet: ${what.join(' and ')} ${campaigns + steps === 1 ? 'uses' : 'use'} this template.`
    : errorMessage(error, 'That use cannot go yet: something still to send uses this template.');
}

/** New — name, purpose, usages and tags — or the details of one, the same form without the tags
 *  (the header's chips edit those). */
export function TemplateFormDialog({
  open,
  onOpenChange,
  template,
  onDone
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Renaming this one; absent for a new template. */
  template?: IMailTemplate;
  onDone: (template: IMailTemplate) => void;
}) {
  const id = React.useId();
  const can = useCan();
  // Reminders take a template only from `mail.templates`.
  const reminders = can('mail.templates');
  const [name, setName] = React.useState('');
  const [purpose, setPurpose] = React.useState('');
  const [usages, setUsages] = React.useState<MailUsage[]>(['lists']);
  const [tags, setTags] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [nameProblem, setNameProblem] = React.useState<Nullable<string>>(null);
  const suggestions = useMailTags();

  React.useEffect(() => {
    if (!open) return;
    setName(template?.name ?? '');
    setPurpose(template?.purpose ?? '');
    setUsages(template?.usages?.length ? template.usages : ['lists']);
    setTags(template?.tags ?? []);
    setRefusal(null);
    setNameProblem(null);
    setBusy(false);
    // Read as the dialog opens: a draft saved under it changes `template`, not what is typed.
  }, [open]);

  const trimmed = name.trim();
  const tooLong = trimmed.length > 120 ? 'At most 120 characters.' : null;
  const blocked = busy || trimmed === '' || Boolean(tooLong) || purpose.length > 500 || usages.length === 0;
  const toggleUsage = (usage: MailUsage, on: boolean) =>
    setUsages((was) => USAGES.map((entry) => entry.value).filter((each) => (each === usage ? on : was.includes(each))));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    setBusy(true);
    setRefusal(null);
    setNameProblem(null);
    try {
      const done = template
        ? await mailTemplatesApi.patch(template.code, { name: trimmed, purpose: purpose.trim(), usages })
        : await mailTemplatesApi.create({ name: trimmed, purpose: purpose.trim(), tags, usages });
      onDone(done);
      onOpenChange(false);
    } catch (error) {
      const named = fieldRefusal(error, 'name');
      if (named) setNameProblem(named);
      else if (error instanceof ApiError && error.status === 409) setRefusal(inUseWords(error));
      else {
        setRefusal(
          fieldRefusal(error, 'tags') ??
            fieldRefusal(error, 'usages') ??
            refusalMessage(error, template ? 'The template could not be saved.' : 'The template could not be created.')
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent data-template-form="">
        <DialogHeader>
          <DialogTitle>{template ? 'Template details' : 'New template'}</DialogTitle>
          <DialogDescription>
            {template ? template.name : 'Its body starts empty; nothing is sent until a version is published.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody as="form" id={`${id}-form`} onSubmit={(event) => void submit(event)} className="text-[0.8125rem]">
          <div className="grid gap-1">
            <label htmlFor={`${id}-name`} className="font-medium">
              Name
            </label>
            <Input
              id={`${id}-name`}
              autoFocus
              value={name}
              maxLength={140}
              aria-invalid={nameProblem || tooLong ? true : undefined}
              onChange={(event) => setName(event.target.value)}
              className="h-8"
            />
            {(nameProblem ?? tooLong) && <p className="text-xs text-destructive">{nameProblem ?? tooLong}</p>}
          </div>
          <div className="grid gap-1">
            <label htmlFor={`${id}-purpose`} className="font-medium">
              What it is for <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id={`${id}-purpose`}
              value={purpose}
              maxLength={500}
              rows={2}
              onChange={(event) => setPurpose(event.target.value)}
            />
          </div>
          <fieldset className="grid gap-1.5" data-template-usages="">
            <legend className="mb-1 flex items-center gap-1 font-medium">
              Where it can be used
              <HelpPopover label="About where a template can be used" className="size-6">
                <p>
                  <em>Email from Lists</em>: offered when a Lists selection is emailed.{' '}
                  <em>Cycle reminders and invitations</em>: offered to a survey&apos;s reminder steps.
                  A template can serve both.
                </p>
              </HelpPopover>
            </legend>
            {USAGES.filter((usage) => usage.value === 'lists' || reminders || usages.includes(usage.value)).map((usage) => {
              const checked = usages.includes(usage.value);
              const only = checked && usages.length === 1;
              return (
                <label key={usage.value} className="flex w-fit items-center gap-2" data-usage-choice={usage.value}>
                  <Checkbox
                    checked={checked}
                    disabled={only || (usage.value === 'reminders' && !reminders)}
                    onCheckedChange={(next) => toggleUsage(usage.value, next === true)}
                  />
                  {usage.label}
                </label>
              );
            })}
          </fieldset>
          {!template && (
            <div className="grid gap-1">
              <span className="flex items-center gap-1">
                <label id={`${id}-tags-label`} htmlFor={`${id}-tags`} className="font-medium">
                  Tags <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <TagsHelp />
              </span>
              <TagInput
                id={`${id}-tags`}
                labelledBy={`${id}-tags-label`}
                value={tags}
                onChange={setTags}
                suggestions={suggestions}
              />
            </div>
          )}
          {refusal && (
            <p role="alert" className="text-destructive">
              {refusal}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={`${id}-form`} size="sm" disabled={blocked}>
            {busy ? 'Saving…' : template ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A custom template's menu: its details, and Archive or Restore. */
export function TemplateMenu({
  template,
  busy,
  leave,
  onRename,
  onArchived
}: {
  template: IMailTemplate;
  busy: boolean;
  /** Runs Archive or Restore once the editor owes no edits — they read it afresh. */
  leave: (go: () => void) => void;
  onRename: () => void;
  onArchived: (message: string) => void;
}) {
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const archived = Boolean(template.archivedAt);
  const toggle = async () => {
    setRefusal(null);
    try {
      if (archived) {
        await mailTemplatesApi.restore(template.code);
        onArchived(`${template.name} is back in the pickers.`);
      } else {
        await mailTemplatesApi.archive(template.code);
        onArchived(`${template.name} is archived: no campaign or reminder can choose it.`);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const steps = (error.problem as { steps?: unknown[] } | undefined)?.steps;
        const count = Array.isArray(steps) ? steps.length : 0;
        setRefusal(
          count > 0
            ? `In use by ${count} reminder step${count === 1 ? '' : 's'} or campaign${count === 1 ? '' : 's'} still to send — change ${count === 1 ? 'it' : 'them'} first.`
            : 'In use by a reminder or a campaign still to send.'
        );
      } else {
        setRefusal(refusalMessage(error, 'The template could not be archived.'));
      }
    }
  };
  return (
    <span className="flex items-center gap-2">
      {refusal && (
        <span role="alert" className="text-xs text-destructive">
          {refusal}
        </span>
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="size-8 p-0" aria-label="More for this template" disabled={busy}>
            <MoreHorizontal className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onSelect={onRename}>
            <Pencil className="size-4" aria-hidden /> Name and use…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => leave(() => void toggle())}>
            {archived ? (
              <>
                <ArchiveRestore className="size-4" aria-hidden /> Restore
              </>
            ) : (
              <>
                <Archive className="size-4" aria-hidden /> Archive
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
